/**
 * Comment screen state (design Comments5): list by tab, the selected comment with its post/thread, the two
 * reply drafts (under the comment · private message), and the rail badge count.
 *
 * Realtime: the inbox socket (stores/inbox.ts onRealtime) — any event of a comment thread refreshes this
 * screen (debounced); a resync refreshes everything. Counts also refresh every 30 s for the rail badge.
 */
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { getApi } from '../api'
import { ApiError } from '../api/client'
import { getCommentsApi } from '../api/comments'
import { MockCommentsApi } from '../api/comments/mock'
import type {
  CannedLine,
  CommentChannelFilter,
  CommentCounts,
  CommentDetail,
  CommentItem,
  CommentSettings,
  CommentSource,
  CommentTab,
  ReplyMode,
  ThreadNode,
} from '../api/comments/types'
import type { RealtimeEvent } from '../api/types'
import { newClientId } from '../lib/send'
import { useInbox } from './inbox'
import { useSession } from './session'

export type DraftOrigin = 'ai' | 'template' | 'canned' | 'manual'

export interface ReplyDraft {
  text: string
  origin: DraftOrigin
  cannedId: string | null
  /** Staff changed the text after it was filled in. */
  edited: boolean
  /** The chat AI's draft replaced the template (only once, and never over staff edits). */
  aiApplied: boolean
}

export interface CommentDrafts {
  pub: ReplyDraft
  dm: ReplyDraft
}

/** The body of a send whose result is unknown (network): pressing again re-posts it with the same client_id. */
interface Pending {
  text: string
  clientId: string
}

const EMPTY_COUNTS: CommentCounts = { todo: 0, done: 0, hidden: 0, oldest_todo_at: null }
const REFRESH_DEBOUNCE_MS = 800
const COUNTS_EVERY_MS = 30_000

const emptyDraft = (): ReplyDraft => ({ text: '', origin: 'manual', cannedId: null, edited: false, aiApplied: false })

export const useComments = defineStore('comments', () => {
  const api = () => getCommentsApi()
  const inbox = useInbox()

  // ------------------------------------------------ state
  const tab = ref<CommentTab>('todo')
  const channel = ref<CommentChannelFilter>('all')
  const query = ref('')
  /** "ดูเฉพาะโพสต์นี้": the list shows the comments of one post/ad. */
  const postFilter = ref<{ id: string; title: string } | null>(null)
  const items = ref<CommentItem[]>([])
  const nextCursor = ref<string | null>(null)
  /** Tab numbers under the list's filters (channel / post). */
  const counts = ref<CommentCounts>({ ...EMPTY_COUNTS })
  /** Whole shop, no filter: the rail badge. */
  const totals = ref<CommentCounts>({ ...EMPTY_COUNTS })
  const sources = ref<CommentSource[]>([])
  const listLoading = ref(false)
  const listError = ref<string | null>(null)
  /** Rows acted on while this tab is open stay in the list (with their new status) until the tab changes. */
  const sticky = ref<Record<string, CommentItem>>({})

  const selectedId = ref<string | null>(null)
  const detail = ref<CommentDetail | null>(null)
  const detailLoading = ref(false)
  const detailError = ref<string | null>(null)
  const mode = ref<ReplyMode>('pub')
  const drafts = ref<Record<string, CommentDrafts>>({})
  const errors = ref<Record<string, Partial<Record<ReplyMode | 'other', string>>>>({})
  const busy = ref<null | ReplyMode | 'hide' | 'skip'>(null)
  const olderItems = ref<ThreadNode[]>([])
  const olderBefore = ref<string | null>(null)
  const olderCount = ref(0)
  const settings = ref<CommentSettings>({ flow_public: true, flow_private: true, ai: true })
  const pending = ref<Record<string, Pending>>({})

  // ------------------------------------------------ getters
  const rows = computed<CommentItem[]>(() => {
    const seen = new Set(items.value.map((i) => i.id))
    const extra = Object.values(sticky.value).filter((s) => !seen.has(s.id))
    const merged = items.value.map((i) => sticky.value[i.id] ?? i).concat(extra)
    const asc = tab.value === 'todo'
    return merged.sort((a, b) => (asc ? a.created_at.localeCompare(b.created_at) : b.created_at.localeCompare(a.created_at)))
  })

  const selected = computed(() => detail.value?.comment ?? null)

  /** Rail badge: someone waits past the red threshold → red dot; any waiting → green dot. */
  const alert = computed<'none' | 'normal' | 'crit'>(() => {
    if (totals.value.todo <= 0) return 'none'
    const oldest = totals.value.oldest_todo_at
    if (oldest && (Date.now() - Date.parse(oldest)) / 1000 > inbox.settings.reply_crit_sec) return 'crit'
    return 'normal'
  })

  const flowLabel = computed(() =>
    settings.value.flow_public && settings.value.flow_private
      ? 'ตอบ 2 ทาง'
      : settings.value.flow_private
        ? 'ตอบทางแชท'
        : 'ตอบใต้คอมเมนต์',
  )

  function draftsOf(id: string): CommentDrafts {
    if (!drafts.value[id]) drafts.value[id] = { pub: emptyDraft(), dm: emptyDraft() }
    return drafts.value[id]!
  }

  const errText = (e: unknown): string => (e instanceof Error ? e.message : String(e))

  // ------------------------------------------------ list
  let listSeq = 0
  async function fetchList(opts: { silent?: boolean } = {}): Promise<void> {
    const seq = ++listSeq
    if (!opts.silent) listLoading.value = true
    try {
      const page = await api().list({
        tab: tab.value, channel: channel.value, q: query.value.trim() || undefined, post: postFilter.value?.id ?? null,
      })
      if (seq !== listSeq) return
      items.value = page.items
      nextCursor.value = page.next_cursor
      counts.value = page.counts
      if (channel.value === 'all' && !postFilter.value) totals.value = page.counts
      listError.value = null
      // keep sticky rows fresh when the server still lists them
      for (const it of page.items) if (sticky.value[it.id]) sticky.value[it.id] = it
      if (!selectedId.value && rows.value[0]) void select(rows.value[0].id)
    } catch (e) {
      if (seq === listSeq) listError.value = errText(e)
    } finally {
      if (seq === listSeq) listLoading.value = false
    }
  }

  const listView = () => `${tab.value}|${channel.value}|${query.value.trim()}|${postFilter.value?.id ?? ''}`

  async function loadMore(): Promise<void> {
    if (!nextCursor.value || listLoading.value) return
    const cursor = nextCursor.value
    const view = listView()
    listLoading.value = true
    try {
      const page = await api().list({
        tab: tab.value, channel: channel.value, q: query.value.trim() || undefined, post: postFilter.value?.id ?? null, cursor,
      })
      if (view !== listView()) return // another tab / filter meanwhile: its own fetchList owns the list
      const have = new Set(items.value.map((i) => i.id))
      items.value = items.value.concat(page.items.filter((i) => !have.has(i.id)))
      nextCursor.value = page.next_cursor
    } catch (e) {
      if (view === listView()) inbox.flash(`โหลดคอมเมนต์เพิ่มไม่สำเร็จ: ${errText(e)}`)
    } finally {
      if (view === listView()) listLoading.value = false
    }
  }

  function setTab(t: CommentTab): void {
    if (tab.value === t) return
    tab.value = t
    sticky.value = {}
    items.value = []
    void fetchList().then(() => {
      // the design keeps the open comment when it is in the new tab, otherwise opens the first one
      if (!rows.value.some((r) => r.id === selectedId.value) && rows.value[0]) void select(rows.value[0].id)
    })
  }

  function setChannel(c: CommentChannelFilter): void {
    if (channel.value === c) return
    channel.value = c
    sticky.value = {}
    void fetchList()
  }

  function setPost(p: { id: string; title: string } | null): void {
    if ((postFilter.value?.id ?? null) === (p?.id ?? null)) return
    postFilter.value = p
    sticky.value = {}
    void fetchList()
  }

  let searchTimer: ReturnType<typeof setTimeout> | null = null
  function setQuery(q: string): void {
    query.value = q
    if (searchTimer) clearTimeout(searchTimer)
    searchTimer = setTimeout(() => void fetchList(), 250)
  }

  async function refreshCounts(): Promise<void> {
    try {
      const c = await api().counts()
      totals.value = { todo: c.todo, done: c.done, hidden: c.hidden, oldest_todo_at: c.oldest_todo_at }
      if (channel.value === 'all' && !postFilter.value) counts.value = { ...totals.value }
      sources.value = c.sources
    } catch {
      // badge only; the next tick retries
    }
  }

  // ------------------------------------------------ detail
  let detailSeq = 0
  async function select(id: string | null, opts: { keepMode?: boolean } = {}): Promise<void> {
    const changed = id !== selectedId.value
    selectedId.value = id
    if (!id) {
      detail.value = null
      return
    }
    if (changed) {
      olderItems.value = []
      olderBefore.value = null
      detailError.value = null
      if (detail.value?.comment.id !== id) detail.value = null
    }
    const seq = ++detailSeq
    detailLoading.value = true
    try {
      const d = await api().get(id)
      if (seq !== detailSeq) return
      applyDetail(d, { fresh: changed && !opts.keepMode })
      markRead(d)
    } catch (e) {
      if (seq === detailSeq) detailError.value = errText(e)
    } finally {
      if (seq === detailSeq) detailLoading.value = false
    }
  }

  function defaultMode(d: CommentDetail): ReplyMode {
    const s = d.settings
    if (!s.flow_public) return 'dm'
    if (!s.flow_private) return 'pub'
    // after the public reply, the next step is the private message (design: ตอบ 2 ทาง)
    return d.comment.replied && !d.comment.private_replied ? 'dm' : d.comment.private_replied && !d.comment.replied ? 'dm' : 'pub'
  }

  /** New detail from the server: keep staff edits, fill untouched drafts from the AI block. */
  function applyDetail(d: CommentDetail, opts: { fresh?: boolean } = {}): void {
    detail.value = d
    settings.value = { ...settings.value, ...d.settings }
    if (opts.fresh) mode.value = defaultMode(d)
    if (mode.value === 'pub' && !d.settings.flow_public) mode.value = 'dm'
    if (mode.value === 'dm' && !d.settings.flow_private) mode.value = 'pub'
    olderCount.value = d.thread.older_count
    if (!olderItems.value.length) olderBefore.value = d.thread.before
    const dr = draftsOf(d.comment.id)
    if (!dr.pub.edited && dr.pub.origin !== 'canned' && !dr.pub.text && d.ai.public_draft) {
      dr.pub = { ...dr.pub, text: d.ai.public_draft, origin: d.ai.draft_source.public === 'ai' ? 'ai' : 'template' }
    }
    if (!dr.dm.edited && dr.dm.origin !== 'canned' && !dr.dm.text && d.ai.private_draft) {
      dr.dm = { ...dr.dm, text: d.ai.private_draft, origin: d.ai.draft_source.private === 'ai' ? 'ai' : 'template' }
    }
    if (d.ai.chat_draft_applies && !dr.dm.aiApplied && !dr.dm.edited && !d.comment.private_replied) {
      void loadChatDraft(d)
    }
    // keep the list row in step
    const row = listItemOf(d)
    const i = items.value.findIndex((x) => x.id === row.id)
    if (i >= 0) items.value.splice(i, 1, row)
    if (sticky.value[row.id]) sticky.value[row.id] = row
  }

  /** A reply/hide/skip result: the panel only when that comment is still the open one, the list row always. */
  function applyIfSelected(d: CommentDetail): boolean {
    if (selectedId.value === d.comment.id) {
      applyDetail(d)
      return true
    }
    const row = listItemOf(d)
    const i = items.value.findIndex((x) => x.id === row.id)
    if (i >= 0) items.value.splice(i, 1, row)
    return false
  }

  function listItemOf(d: CommentDetail): CommentItem {
    const c = d.comment
    return {
      id: c.id, channel: c.channel, state: c.state, author: c.author, text: c.text, has_media: c.has_media,
      created_at: c.created_at, edited: c.edited, unread: c.unread, replied: c.replied,
      private_replied: c.private_replied, skipped: c.skipped, hidden: c.hidden, intent: c.intent,
      conversation_id: c.conversation_id, post: { id: d.post.id, title: d.post.title, is_ad: d.post.is_ad },
    }
  }

  /** The chat's AI already drafted an answer to this comment (its thread's newest customer message). */
  async function loadChatDraft(d: CommentDetail): Promise<void> {
    const conv = d.comment.conversation_id
    if (!conv) return
    try {
      const r = await getApi().getDraft(conv)
      const text = r.status === 'ready' ? r.ai?.draft?.trim() : null
      const dr = draftsOf(d.comment.id)
      if (text && !dr.dm.edited && dr.dm.origin !== 'canned' && !d.comment.private_replied) {
        dr.dm = { ...dr.dm, text, origin: 'ai', aiApplied: true }
      }
    } catch {
      // the template stays
    }
  }

  function markRead(d: CommentDetail): void {
    if (!d.comment.unread) return
    const a = api()
    const conv = d.comment.conversation_id
    if (a instanceof MockCommentsApi) a.markRead(d.comment.id)
    else if (conv && inbox.convs[conv]?.unread) void inbox.markRead(conv) // also clears the inbox row
    else if (conv) void getApi().markRead(conv).catch(() => undefined)
    const i = items.value.findIndex((x) => x.id === d.comment.id)
    if (i >= 0) items.value[i] = { ...items.value[i]!, unread: false }
    if (detail.value) detail.value.comment.unread = false
  }

  async function loadOlder(): Promise<void> {
    const d = detail.value
    if (!d || !olderBefore.value) return
    try {
      const page = await api().thread(d.comment.id, olderBefore.value)
      if (detail.value?.comment.id !== d.comment.id) return // another comment opened meanwhile
      const shown = new Set([...d.thread.items, ...olderItems.value].map((n) => n.id))
      olderItems.value = page.items.filter((n) => !shown.has(n.id)).concat(olderItems.value)
      olderBefore.value = page.before
      olderCount.value = page.before ? page.older_count : 0
    } catch (e) {
      inbox.flash(`โหลดคอมเมนต์ก่อนหน้าไม่สำเร็จ: ${errText(e)}`)
    }
  }

  // ------------------------------------------------ drafts
  function setText(m: ReplyMode, text: string): void {
    const id = selectedId.value
    if (!id) return
    const dr = draftsOf(id)
    dr[m] = { ...dr[m], text, edited: true, origin: text.trim() ? dr[m].origin : 'manual', cannedId: text.trim() ? dr[m].cannedId : null }
    if (errors.value[id]?.[m]) errors.value[id] = { ...errors.value[id], [m]: undefined }
  }

  /** A canned line replaces an untouched draft, otherwise it is added at the end. */
  function useCanned(m: ReplyMode, c: CannedLine): void {
    const id = selectedId.value
    if (!id) return
    const dr = draftsOf(id)
    const cur = dr[m]
    const text = !cur.edited || !cur.text.trim() ? c.text : `${cur.text.trim()} ${c.text}`
    dr[m] = { ...cur, text, origin: 'canned', cannedId: c.id, edited: cur.edited && !!cur.text.trim() }
  }

  // ------------------------------------------------ actions
  function stick(d: CommentDetail): void {
    sticky.value[d.comment.id] = listItemOf(d)
  }

  function setError(id: string, key: ReplyMode | 'other', msg: string | undefined): void {
    errors.value[id] = { ...(errors.value[id] ?? {}), [key]: msg }
  }

  async function send(): Promise<boolean> {
    const d = detail.value
    const m = mode.value
    if (!d || busy.value) return false
    const id = d.comment.id
    const dr = draftsOf(id)[m]
    const text = dr.text.trim()
    if (!text) return false
    const key = `${id}:${m}`
    // same text again after an unknown result (network): same client_id → the server answers the first result
    const prev = pending.value[key]
    const clientId = prev && prev.text === text ? prev.clientId : newClientId()
    pending.value[key] = { text, clientId }
    busy.value = m
    setError(id, m, undefined)
    try {
      const body = { text, client_id: clientId, from_ai_draft: dr.origin === 'ai' || dr.origin === 'template', canned_id: dr.cannedId }
      const out = m === 'pub' ? await api().reply(id, body) : await api().privateReply(id, body)
      delete pending.value[key]
      stick(out)
      // the staff may have opened another comment while Meta answered: only its row changes then
      if (!applyIfSelected(out)) return true
      if (m === 'pub' && out.settings.flow_private && !out.comment.private_replied && out.rules.private.can) mode.value = 'dm'
      void refreshCounts()
      void fetchList({ silent: true })
      return true
    } catch (e) {
      const status = e instanceof ApiError ? e.status : 0
      if (status !== 0) delete pending.value[key] // the server answered: a new press is a new send
      setError(id, m, errText(e))
      if ((status === 409 || status === 422) && selectedId.value === id) void select(id, { keepMode: true })
      return false
    } finally {
      busy.value = null
    }
  }

  async function setHidden(hidden: boolean): Promise<void> {
    const d = detail.value
    if (!d || busy.value) return
    busy.value = 'hide'
    setError(d.comment.id, 'other', undefined)
    try {
      const out = await api().hide(d.comment.id, hidden)
      stick(out)
      applyIfSelected(out)
      void refreshCounts()
      void fetchList({ silent: true })
    } catch (e) {
      setError(d.comment.id, 'other', errText(e))
    } finally {
      busy.value = null
    }
  }

  async function setSkip(skip: boolean): Promise<void> {
    const d = detail.value
    if (!d || busy.value) return
    busy.value = 'skip'
    setError(d.comment.id, 'other', undefined)
    try {
      const out = await api().skip(d.comment.id, skip)
      stick(out)
      applyIfSelected(out)
      void refreshCounts()
      void fetchList({ silent: true })
    } catch (e) {
      setError(d.comment.id, 'other', errText(e))
    } finally {
      busy.value = null
    }
  }

  async function loadSettings(): Promise<void> {
    try {
      settings.value = await api().getSettings()
    } catch {
      // defaults stay
    }
  }

  // ------------------------------------------------ realtime + lifecycle
  let refreshTimer: ReturnType<typeof setTimeout> | null = null
  const refreshDelayMs = ref(REFRESH_DEBOUNCE_MS)
  /** Is the comment screen open (list + detail refresh) or only the rail badge. */
  const viewing = ref(false)

  function scheduleRefresh(): void {
    if (refreshTimer) clearTimeout(refreshTimer)
    refreshTimer = setTimeout(() => {
      refreshTimer = null
      void refreshCounts()
      if (!viewing.value) return
      void fetchList({ silent: true })
      if (selectedId.value && !busy.value) void select(selectedId.value, { keepMode: true })
    }, refreshDelayMs.value)
  }

  function onEvent(e: RealtimeEvent | null): void {
    if (e === null || e.conversation.kind === 'comment') scheduleRefresh()
  }

  let started = false
  let off: (() => void) | null = null
  let periodic: ReturnType<typeof setInterval> | null = null
  let stopWatch: (() => void) | null = null

  /** Rail badge + realtime. Idempotent (AppRail calls it on every screen). */
  function start(): void {
    if (started) return
    started = true
    const session = useSession()
    off = inbox.onRealtime(onEvent)
    void refreshCounts()
    void loadSettings()
    periodic = setInterval(() => {
      if (session.state === 'in') void refreshCounts()
    }, COUNTS_EVERY_MS)
    stopWatch = watch(
      () => session.state,
      (s) => {
        if (s !== 'in') stop()
      },
    )
  }

  function stop(): void {
    started = false
    off?.()
    off = null
    if (periodic) clearInterval(periodic)
    periodic = null
    stopWatch?.()
    stopWatch = null
    if (refreshTimer) clearTimeout(refreshTimer)
    refreshTimer = null
  }

  /** The comment screen opened (optionally on one comment). */
  async function open(commentId: string | null = null): Promise<void> {
    viewing.value = true
    start()
    if (commentId) void select(commentId)
    await fetchList()
  }

  function close(): void {
    viewing.value = false
  }

  return {
    // state
    tab,
    channel,
    query,
    postFilter,
    items,
    nextCursor,
    counts,
    totals,
    sources,
    listLoading,
    listError,
    sticky,
    selectedId,
    detail,
    detailLoading,
    detailError,
    mode,
    drafts,
    errors,
    busy,
    olderItems,
    olderBefore,
    olderCount,
    settings,
    refreshDelayMs,
    viewing,
    // getters
    rows,
    selected,
    alert,
    flowLabel,
    // actions
    draftsOf,
    fetchList,
    loadMore,
    setTab,
    setChannel,
    setPost,
    setQuery,
    refreshCounts,
    select,
    loadOlder,
    setText,
    useCanned,
    send,
    setHidden,
    setSkip,
    loadSettings,
    onEvent,
    start,
    stop,
    open,
    close,
  }
})
